import { TOOL_OPTIONS } from "@/constants/tool";
import env from "@/env";
import { baseUrl, explorerPrefixedPath, urlWithCurrentQuery } from ".";
import { isExplorerOnlyMode } from "./explorerOnlyMode";
import { redirectOnce } from "./location";

export const getToolUrl = (value: string, path: string) => {
  if (!env.SECTION_SUBDOMAINS_ENABLED) {
    return `${baseUrl()}${path}`;
  }

  return `${window.location.protocol}//${value}.${env.APP_DOMAIN}${
    window.location.port ? `:${window.location.port}` : ``
  }${path}`;
};

export const joinDomainPath = (domain: string, path: string) => {
  const base = domain.replace(/\/$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
};

/** Cloud dashboard on the cloud domain (used from EXPLORER_ONLY_MODE cross-links). */
export const getCloudDashboardUrl = () => {
  if (env.CLOUD_DOMAIN) {
    return joinDomainPath(env.CLOUD_DOMAIN, "/cloud/dashboard");
  }
  return getAppDomainToolUrl("cloud", "/cloud/dashboard");
};

/** Intelligence models page on the AI domain (used from EXPLORER_ONLY_MODE cross-links). */
export const getIntelligenceModelsUrl = () => {
  if (env.INTELLIGENCE_DOMAIN) {
    return joinDomainPath(env.INTELLIGENCE_DOMAIN, "/ai/models");
  }
  return getAppDomainToolUrl("ai", "/ai/models");
};

/** Resolve Io Elements tool card href (explorer-only uses explicit product domains). */
export const getIoElementHref = (toolValue: string, defaultHref: string) => {
  if (isExplorerOnlyMode()) {
    if (toolValue === "explorer") {
      return defaultHref;
    }
    if (toolValue === "ai") {
      return getIntelligenceModelsUrl();
    }
    if (toolValue === "cloud") {
      return getCloudDashboardUrl();
    }
  }

  return getAppDomainToolUrl(toolValue, defaultHref);
};

/** Resolve account-menu paths that leave explorer-only mode for the main app. */
export const resolveExplorerOnlyExternalUrl = (path: string) => {
  if (path.startsWith("/cloud/")) {
    if (env.CLOUD_DOMAIN) {
      return joinDomainPath(env.CLOUD_DOMAIN, path);
    }
    return getAppDomainToolUrl("cloud", path);
  }

  if (env.EXPLORER_DOMAIN) {
    return joinDomainPath(env.EXPLORER_DOMAIN, path);
  }

  return getAppDomainToolUrl("explorer", path);
};

/** Full URL on `APP_DOMAIN` for cross-host tool links (e.g. explorer header → Intelligence). */
export const getAppDomainToolUrl = (toolValue: string, path: string) => {
  if (path.indexOf("://") > -1) {
    return path;
  }

  const protocol = typeof window !== "undefined" ? window.location.protocol : "https:";
  const port =
    typeof window !== "undefined" && window.location.port ? `:${window.location.port}` : "";

  if (env.SECTION_SUBDOMAINS_ENABLED) {
    return `${protocol}//${toolValue}.${env.APP_DOMAIN}${port}${path}`;
  }

  return `${protocol}//${env.APP_DOMAIN}${port}${path}`;
};

export const getSubdomain = () => {
  const subdomain = window.location.hostname.split(".")[0];
  return subdomain;
};

/** Default in-app path for the current section subdomain (first matching `TOOL_OPTIONS` entry). */
export const getSubdomainToolHomePath = (): string | null => {
  if (!env.SECTION_SUBDOMAINS_ENABLED || typeof window === "undefined") {
    return null;
  }
  const subdomain = getSubdomain();
  const tool = TOOL_OPTIONS.find((t) => t.value === subdomain);
  if (!tool?.href) {
    return null;
  }
  return explorerPrefixedPath(tool.href);
};

export const isOnLoginSubdomain = () => {
  const subdomain = getSubdomain();
  return [
    "id",
    ...(env.FEATUREFLAG_EXPLORER_PUBLIC ? ["explorer"] : []),
    ...(env.FEATUREFLAG_DELAYED_LOGIN ? ["cloud", "worker", "staking", "ai"] : []),
    env.LOGIN_SUBDOMAIN
  ].includes(subdomain);
};

export const redirectToLogin = (redirect: string) => {
  const redirectUrl = urlWithCurrentQuery(
    getToolUrl(env.LOGIN_SUBDOMAIN, explorerPrefixedPath("/login")),
    { redirect }
  );
  console.info("redirect as unauthorised + wrong subdomain", redirectUrl);
  redirectOnce(redirectUrl);
};
